import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import PageSEO from '@/components/feature/PageSEO';
import { getSolutionPage, type SolutionPage } from '@/content/solutionPages';
import { LOCAL_BUSINESS_ID, ORGANIZATION_ID, SITE_URL, type StructuredData } from '@/lib/seo';
import Footer from '@/pages/home/components/Footer';

function buildSolutionSchema(page: SolutionPage): StructuredData {
  const url = `${SITE_URL}${page.path}`;
  const service: StructuredData = {
    '@type': 'Service',
    '@id': `${url}#service`,
    name: page.h1,
    description: page.description,
    url,
    provider: { '@id': LOCAL_BUSINESS_ID },
    areaServed: page.path.includes('victoria') || page.path.includes('melbourne') || page.path.includes('granny')
      ? { '@type': 'AdministrativeArea', name: 'Victoria' }
      : { '@type': 'Country', name: 'Australia' },
    audience: { '@type': 'Audience', audienceType: page.audience },
  };
  const graph: StructuredData[] = [service];

  if (page.model === 'M5 Residential') {
    graph.push({
      '@type': 'Product',
      '@id': `${url}#m5-residential`,
      name: 'M5 Residential',
      description: page.intro,
      image: `${SITE_URL}${page.image}`,
      url,
      brand: { '@id': ORGANIZATION_ID },
      category: 'Modular small second home',
      floorSize: { '@type': 'QuantitativeValue', value: 26, unitCode: 'MTK' },
      additionalProperty: [
        ...page.amenities.map((amenity) => ({ '@type': 'PropertyValue', name: amenity, value: 'Included in the residential configuration' })),
        { '@type': 'PropertyValue', name: 'Documentation status', value: page.disclosure },
      ],
    });
  }

  graph.push({
    '@type': 'FAQPage',
    '@id': `${url}#faq`,
    mainEntity: page.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  });

  return { '@context': 'https://schema.org', '@graph': graph };
}

export default function SolutionPage() {
  const { pathname } = useLocation();
  const page = getSolutionPage(pathname);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const schema = useMemo(() => page ? buildSolutionSchema(page) : undefined, [page]);

  if (!page) return null;

  return (
    <>
      <PageSEO
        title={page.title}
        description={page.description}
        canonicalPath={page.path}
        ogImage={`${SITE_URL}${page.image}`}
        jsonLd={schema}
      />
      <div className="min-h-screen bg-[#05070A] text-white overflow-x-hidden">
        <nav className="fixed top-0 left-0 right-0 z-50 bg-black/85 backdrop-blur-xl border-b border-cyan-400/15">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
            <Link to="/" aria-label="Nexa Pods home"><img src="/images/c719b13a28cc.webp" alt="Nexa Pods" width="180" height="76" className="h-14 w-auto" /></Link>
            <div className="hidden lg:flex items-center gap-7 text-sm">
              <Link to="/granny-flats-victoria" className="text-gray-300 hover:text-cyan-300">Granny Flats</Link>
              <Link to="/glamping-pods-australia" className="text-gray-300 hover:text-cyan-300">Glamping</Link>
              <Link to="/backyard-office-pods-melbourne" className="text-gray-300 hover:text-cyan-300">Office Pods</Link>
              <Link to="/product-range" className="text-gray-300 hover:text-cyan-300">Products</Link>
              <Link to="/modular-pod-buyers-guide" className="text-gray-300 hover:text-cyan-300">Buyer Guide</Link>
              <Link to="/contact" className="px-5 py-2.5 rounded-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300">Discuss Your Site</Link>
            </div>
            <button type="button" onClick={() => setMobileMenuOpen((open) => !open)} aria-expanded={mobileMenuOpen} aria-label="Toggle navigation" className="lg:hidden w-11 h-11 border border-cyan-400/30 rounded-full text-cyan-300">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
            </button>
          </div>
          {mobileMenuOpen && (
            <div className="lg:hidden px-6 pb-6 bg-black border-t border-white/10 space-y-2">
              {[['Granny Flats', '/granny-flats-victoria'], ['Glamping', '/glamping-pods-australia'], ['Office Pods', '/backyard-office-pods-melbourne'], ['Products', '/product-range'], ['Buyer Guide', '/modular-pod-buyers-guide'], ['Contact', '/contact']].map(([label, path]) => (
                <Link key={path} to={path} onClick={() => setMobileMenuOpen(false)} className="block py-3 text-gray-200 border-b border-white/5">{label}</Link>
              ))}
            </div>
          )}
        </nav>

        <main>
          <section className="relative pt-32 sm:pt-40 pb-16 sm:pb-24">
            <div className="absolute inset-0 overflow-hidden pointer-events-none"><div className="absolute -top-48 right-[-10%] w-[38rem] h-[38rem] bg-cyan-500/10 rounded-full blur-[120px]" /></div>
            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-6">
                <p className="text-xs tracking-[0.28em] uppercase text-cyan-300 mb-6">{page.eyebrow}</p>
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-[-0.04em] leading-[0.96] mb-7">{page.h1}</h1>
                <p className="text-lg sm:text-xl text-gray-300 leading-relaxed max-w-2xl">{page.intro}</p>
                <div className="flex flex-wrap gap-3 mt-9">
                  <Link to="/contact" className="px-7 py-3.5 bg-cyan-400 text-black rounded-full font-semibold hover:bg-cyan-300">Request a Site Review</Link>
                  <Link to="/modular-pod-buyers-guide" className="px-7 py-3.5 border border-white/20 rounded-full text-white hover:border-cyan-300">Read the Buyer Guide</Link>
                </div>
              </div>
              <div className="lg:col-span-6 relative">
                <div className="absolute -inset-4 bg-cyan-400/10 blur-3xl rounded-[2rem]" />
                <img src={page.image} alt={page.imageAlt} width="1200" height="900" fetchPriority="high" decoding="async" className="relative w-full aspect-[4/3] object-cover rounded-[1.5rem] border border-cyan-300/20" />
                {(page.model || page.floorArea) && (
                  <div className="absolute left-4 right-4 bottom-4 p-4 sm:p-5 rounded-xl bg-black/75 backdrop-blur-xl border border-white/10 flex flex-wrap gap-x-8 gap-y-2">
                    {page.model && <div><p className="text-[10px] tracking-widest uppercase text-gray-500">Lead model</p><p className="text-cyan-300 font-medium">{page.model}</p></div>}
                    {page.floorArea && <div><p className="text-[10px] tracking-widest uppercase text-gray-500">Floor area</p><p className="text-white font-medium">{page.floorArea}</p></div>}
                  </div>
                )}
              </div>
            </div>
          </section>

          {page.disclosure && (
            <section className="border-y border-amber-300/20 bg-amber-300/[0.06]">
              <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
                <p className="text-sm text-amber-100 flex flex-wrap gap-x-1.5">
                  <strong className="text-amber-300">Current documentation status:</strong>
                  <span>{page.disclosure}</span>
                </p>
                <a href="https://www.planning.vic.gov.au/guides-and-resources/guides/all-guides/small-second-homes" target="_blank" rel="noreferrer" className="text-sm text-amber-300 hover:text-amber-200 whitespace-nowrap">Victorian guidance ↗</a>
              </div>
            </section>
          )}

          <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
            <div className="grid lg:grid-cols-12 gap-12">
              <div className="lg:col-span-4"><p className="text-xs tracking-[0.26em] uppercase text-cyan-300 mb-5">Designed for</p><h2 className="text-3xl sm:text-4xl font-light leading-tight">A clear use, not a generic shell.</h2><p className="mt-6 text-gray-400 leading-relaxed">{page.audience}</p></div>
              <div className="lg:col-span-8 grid sm:grid-cols-3 gap-4">
                {page.benefits.map((benefit, index) => (
                  <div key={benefit.title} className="p-6 sm:p-7 bg-white/[0.035] border border-white/10 rounded-2xl"><span className="text-xs text-cyan-400">{String(index + 1).padStart(2, '0')}</span><h3 className="text-xl mt-8 mb-4">{benefit.title}</h3><p className="text-sm text-gray-400 leading-relaxed">{benefit.text}</p></div>
                ))}
              </div>
            </div>
          </section>

          <section className="bg-[#0A0E13] border-y border-white/5">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid lg:grid-cols-12 gap-12">
              <div className="lg:col-span-4"><p className="text-xs tracking-[0.26em] uppercase text-cyan-300 mb-5">Configuration</p><h2 className="text-3xl sm:text-4xl font-light">What this solution can include</h2></div>
              <ul className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
                {page.amenities.map((amenity) => <li key={amenity} className="bg-[#0A0E13] p-6 flex items-center gap-4"><span className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_12px_rgba(34,211,238,0.9)]" /><span className="text-gray-200">{amenity}</span></li>)}
              </ul>
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
            <div className="flex flex-col lg:flex-row gap-8 lg:items-end justify-between mb-12"><div><p className="text-xs tracking-[0.26em] uppercase text-cyan-300 mb-5">Project pathway</p><h2 className="text-3xl sm:text-5xl font-light">From enquiry to a site-specific plan.</h2></div><p className="text-sm text-gray-500 max-w-md">Every project scope depends on the selected product, property access, foundations, connections, installation and approvals.</p></div>
            <ol className="grid md:grid-cols-4 border-t border-l border-white/10">
              {page.process.map((step, index) => <li key={step.title} className="p-6 sm:p-8 border-r border-b border-white/10 min-h-56"><span className="text-cyan-400 text-sm">{String(index + 1).padStart(2, '0')}</span><h3 className="text-xl mt-10 mb-4">{step.title}</h3><p className="text-sm text-gray-400 leading-relaxed">{step.text}</p></li>)}
            </ol>
          </section>

          <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32">
            <p className="text-xs tracking-[0.26em] uppercase text-cyan-300 mb-5">Questions</p><h2 className="text-3xl sm:text-5xl font-light mb-10">Before you plan the site.</h2>
            <div className="divide-y divide-white/10 border-y border-white/10">{page.faq.map((item) => <details key={item.question} className="group py-6"><summary className="cursor-pointer list-none flex justify-between gap-6 text-lg"><span>{item.question}</span><span className="text-cyan-400 group-open:rotate-45 transition-transform">+</span></summary><p className="pt-4 text-gray-400 leading-relaxed max-w-3xl">{item.answer}</p></details>)}</div>
          </section>

          <section className="px-5 sm:px-8 pb-24"><div className="max-w-7xl mx-auto rounded-[2rem] border border-cyan-300/20 bg-gradient-to-br from-cyan-400/15 to-blue-500/5 p-8 sm:p-14 flex flex-col lg:flex-row gap-8 lg:items-center justify-between"><div><p className="text-cyan-300 text-sm mb-3">Bring the site into the conversation.</p><h2 className="text-3xl sm:text-5xl font-light">Get a proposal built around your project.</h2></div><Link to="/contact" className="px-8 py-4 bg-cyan-400 text-black text-center rounded-full font-semibold hover:bg-cyan-300 whitespace-nowrap">Start an Enquiry</Link></div></section>
        </main>
        <Footer />
      </div>
    </>
  );
}
